import { useState, useEffect } from 'react'
import { Bell, RefreshCw, AlertCircle, Trash2, Plus, Mail } from 'lucide-react'
import { usageService, SpendAlertsData, SpendAlertProject } from '../../services/usageService'
import { formatUsd } from '../../lib/formatters'

export function SpendAlertsCard() {
  const [data, setData] = useState<SpendAlertsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState<string | null>(null)
  const [thresholdInput, setThresholdInput] = useState('')
  const [recipientsInput, setRecipientsInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const result = await usageService.getSpendAlerts()
      setData(result)
    } catch (err) {
      console.error('Failed to load spend alerts:', err)
      setError('Não foi possível carregar spend alerts')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const handleCreate = async (projectId: string) => {
    const threshold = parseFloat(thresholdInput.replace(',', '.'))
    const recipients = recipientsInput.split(',').map(r => r.trim()).filter(Boolean)
    if (isNaN(threshold) || threshold <= 0 || recipients.length === 0) return

    try {
      setSaving(true)
      await usageService.createSpendAlert(projectId, threshold, recipients)
      setCreating(null)
      setThresholdInput('')
      setRecipientsInput('')
      await load()
    } catch (err) {
      console.error('Failed to create spend alert:', err)
      setError('Erro ao criar alerta — verifique os e-mails informados')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (projectId: string, alertId: string) => {
    try {
      setDeleting(alertId)
      await usageService.deleteSpendAlert(projectId, alertId)
      await load()
    } catch (err) {
      console.error('Failed to delete spend alert:', err)
    } finally {
      setDeleting(null)
    }
  }

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-16 bg-gray-100 rounded"></div>
        </div>
      </div>
    )
  }

  if (error || !data?.configured || data.error) {
    return (
      <div className="bg-white shadow rounded-lg p-4 flex items-center gap-2">
        <AlertCircle className="h-4 w-4 text-yellow-600 flex-shrink-0" />
        <p className="text-xs text-yellow-800">
          {error || data?.error || (
            <>Spend alerts indisponíveis — configure <code className="font-mono">OPENAI_ADMIN_KEY</code> no auth-service.</>
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-4 sm:px-6 py-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Bell className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Spend Alerts — OpenAI</h3>
              <p className="text-xs text-gray-500">
                Alertas por e-mail quando o gasto mensal de um projeto atinge o valor definido.
                É notificação, não corte de uso.
              </p>
            </div>
          </div>
          <button
            onClick={load}
            className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-md transition-colors"
            title="Atualizar"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>

        {data.projects.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">Nenhum projeto ativo encontrado na organização OpenAI.</p>
        ) : (
          <div className="mt-4 space-y-3">
            {data.projects.map((project: SpendAlertProject) => (
              <div key={project.id} className="border border-gray-100 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-sm font-medium text-gray-900">{project.name}</span>
                    <span className="ml-2 text-[10px] text-gray-400 font-mono">{project.id}</span>
                  </div>
                  {creating !== project.id && (
                    <button
                      onClick={() => {
                        setCreating(project.id)
                        setThresholdInput('')
                        setRecipientsInput('')
                      }}
                      className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-md transition-colors"
                    >
                      <Plus className="h-3 w-3" /> Novo alerta
                    </button>
                  )}
                </div>

                {project.alerts.length === 0 && creating !== project.id && (
                  <p className="text-xs text-gray-400">Nenhum alerta configurado.</p>
                )}

                {project.alerts.map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between py-1.5 border-t border-gray-50 first:border-t-0">
                    <div className="flex items-center gap-3 text-xs text-gray-600">
                      <span className="font-semibold text-orange-600">{formatUsd(alert.thresholdUsd)}</span>
                      <span className="text-gray-400">/ {alert.interval === 'month' ? 'mês' : alert.interval}</span>
                      <span className="flex items-center gap-1 text-gray-500">
                        <Mail className="h-3 w-3" />
                        {alert.recipients.join(', ') || 'owners da org'}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(project.id, alert.id)}
                      disabled={deleting === alert.id}
                      className="p-1 text-gray-300 hover:text-red-500 transition-colors disabled:opacity-50"
                      title="Remover alerta"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}

                {creating === project.id && (
                  <div className="mt-2 bg-amber-50 rounded-md p-2.5 space-y-2">
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="number"
                        min="1"
                        step="10"
                        value={thresholdInput}
                        onChange={(e) => setThresholdInput(e.target.value)}
                        placeholder="Valor (USD)"
                        className="w-full sm:w-32 text-sm border border-gray-300 rounded px-2 py-1"
                        autoFocus
                      />
                      <input
                        type="text"
                        value={recipientsInput}
                        onChange={(e) => setRecipientsInput(e.target.value)}
                        placeholder="E-mails separados por vírgula"
                        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleCreate(project.id)}
                        disabled={saving}
                        className="text-xs bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                      >
                        {saving ? 'Criando...' : 'Criar alerta'}
                      </button>
                      <button
                        onClick={() => setCreating(null)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
